import React, { useEffect, useState } from 'react'
import { connect } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { formatRelative } from '../../i18n/format'
import { getStoryCorrections, setCorrections, setCurrentCorrection } from '../../redux/actions/dashboardAction'
import InfiniteScroll from 'react-infinite-scroll-component'
import { levelLabelFromEnum, levelLabelFromWordCount, localizedLevelLabel } from '../story-card/constants'
import { normalizeSpeakingMode, speakingModeShortLabel } from './WritingSpeaking'

const sidebarModeLabel = (correction: any): string => {
  const mode = normalizeSpeakingMode(correction?.data?.speakingMode)
  return mode ? speakingModeShortLabel(mode) : ''
}

const getCorrectionLevelLabel = (correction: any): string => {
  // Prefer the backend's stored combined level (story_level); fall back to
  // deriving from unique_word_count, then to the coarse Prismic level.
  const fromStoryLevel = levelLabelFromEnum(correction.data?.story_level)
  if (fromStoryLevel) return fromStoryLevel

  const fromWordCount = levelLabelFromWordCount(Number(correction.data?.unique_word_count))
  if (fromWordCount) return fromWordCount

  return localizedLevelLabel(null, correction.data?.level) || ''
}

// The brand name, not copy: it reads the same in every language.
const BRAND = 'maayot'

const Sidebar: React.FC = (props: any) => {
  const { t } = useTranslation('dashboard')
  const perPage = 10
  const [page, setPage] = useState(1)
  const [totalCorrection, setTotalCorrection] = useState(0)
  const [searchString, setSearchString] = useState('')

  useEffect(() => {
    if (props.corrections.length && !props?.currentStoryCorrection) setCurrentCorrection(props.corrections[0])
  }, [props.corrections])

  const setCorrection = (correction) => {
    setCurrentCorrection(correction)
  }

  const fetchData = async (isSearch) => {
    const currentCorrections = !isSearch ? [...props.corrections] : []
    const currentPage = !isSearch ? page : 1
    if (isSearch) setPage(1)
    await getStoryCorrections(currentPage, perPage, searchString).then((res) => {
      setTotalCorrection(res?.count)
      const allCorrections = currentCorrections.concat([...res?.answers])
      setCorrections(allCorrections)

      if (allCorrections.length < res?.count) setPage(page + 1)
    })
  }

  const onTextChange = (e) => {
    setSearchString(e.target.value)
  }

  const clearSearch = () => {
    setSearchString('')
  }

  useEffect(() => {
    if (!searchString.length) {
      fetchData(true)
    } else
      setTimeout(() => {
        if (searchString.length > 2) {
          fetchData(true)
        }
      }, 1500)
  }, [searchString])

  return (
    <>
      <div className='left-side'>
        <div className='sidebar-logo mb-10 mt-10 pl-10'>
          <span className='color-white'>
            <b>{BRAND}</b>
          </span>
        </div>

        <div className='searchbar-div mt-10 mb-10 text-lightgray'>
          <i className='fa fa-search searchicon'></i>
          <input
            type='text'
            placeholder={t('teacher.sidebar.searchPlaceholder')}
            className='search'
            value={searchString}
            onChange={onTextChange}
          />
          {searchString !== '' ? <i className='fa fa-times clear-search-icon m-pointer' onClick={clearSearch}></i> : ''}
        </div>

        <div className='dashboard-sidebar' id='scrollableDiv'>
          <InfiniteScroll
            dataLength={props.corrections.length}
            next={() => fetchData(false)}
            hasMore={props.corrections.length < totalCorrection}
            scrollableTarget='scrollableDiv'
            loader={
              <div className='assignment-main-box'>
                <div className='assignment-box w-d-flex align-items-center'>
                  <div className='user-content'>
                    <h5 className='color-white'>{t('teacher.sidebar.loading')}</h5>
                  </div>
                </div>
              </div>
            }
          >
            {props.corrections?.map((correction, index) => (
              <div
                key={index}
                className={`assignment-main-box
                  ${
                    `${correction?.data?.memberId} + ${correction?.data?.storyId}` ===
                    `${props?.currentStoryCorrection?.data?.memberId} + ${props?.currentStoryCorrection?.data?.storyId}`
                      ? correction.data.isOpen
                        ? 'activate-green'
                        : 'activate-orange'
                      : ''
                  }`}
                onClick={() => setCorrection(correction)}
              >
                <div className='assignment-box w-d-flex align-items-center'>
                  <div className='user-content'>
                    <span
                      className={
                        correction.data.isOpen
                          ? 'text-uppercase green-active active-box'
                          : 'text-uppercase orange-active active-box'
                      }
                    >
                      {correction.data.isOpen ? t('teacher.sidebar.open') : t('teacher.sidebar.closed')}
                    </span>
                    {correction.data.is_taster && (
                      /* One-time free correction from the re-engagement drip: this
                         learner is meeting teacher feedback for the first time. */
                      <span className='text-uppercase active-box taster-badge'>{t('teacher.sidebar.taster')}</span>
                    )}
                    {sidebarModeLabel(correction) && (
                      <span className='active-box dash-ws-mode-badge'>
                        <i className='fa fa-microphone' aria-hidden={true} />
                        {sidebarModeLabel(correction)}
                      </span>
                    )}
                    <div className='user-title color-white'>{correction.data.memberName}</div>
                    <span className='user-activate-time'>
                      {formatRelative(parseInt(String(correction?.data?.createdAt), 10))}{' '}
                      <span className='dot text-capitalize'>{getCorrectionLevelLabel(correction)}</span>
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </InfiniteScroll>
        </div>
      </div>
    </>
  )
}

const mapStateToProps = (state: any) => ({
  corrections: state.dashboard.corrections,
  currentStoryCorrection: state.dashboard.currentStoryCorrection,
})

export default connect(mapStateToProps)(Sidebar)
